import type { App, DirectiveBinding } from 'vue';
import { usePermissionStore } from '@/stores/usePermissionStore';
import { useAuthStore } from '@/stores/useAuthStore';

/**
 * 权限指令值类型
 */
type PermissionValue =
  | string
  | string[]
  | {
      permission?: string | string[];
      /** demo 矩阵叶子码 */
      matrix?: string | string[];
      role?: string | string[];
      requireAll?: boolean;
      mode?: 'hide' | 'disable';
    };

function resolveHasAccess(binding: DirectiveBinding<PermissionValue>): {
  hasAccess: boolean;
  mode: 'hide' | 'disable';
} {
  const permissionStore = usePermissionStore();
  const authStore = useAuthStore();
  let mode: 'hide' | 'disable' = 'hide';

  if (!authStore.isAuthenticated) {
    return { hasAccess: false, mode };
  }

  let hasAccess = false;

  if (typeof binding.value === 'string') {
    const code = binding.value;
    hasAccess = code.includes('-matrix-')
      ? permissionStore.checkMatrixPermission(code)
      : permissionStore.checkPermission(code);
  } else if (Array.isArray(binding.value)) {
    const codes = binding.value;
    const allMatrix = codes.every((c) => c.includes('-matrix-'));
    hasAccess = allMatrix
      ? codes.some((c) => permissionStore.checkMatrixPermission(c))
      : permissionStore.hasPermissions(codes, false);
  } else if (typeof binding.value === 'object' && binding.value !== null) {
    const config = binding.value;
    mode = config.mode || 'hide';
    const requireAll = !!config.requireAll;

    if (config.matrix) {
      const leaves = Array.isArray(config.matrix) ? config.matrix : [config.matrix];
      hasAccess = requireAll
        ? leaves.every((c) => permissionStore.checkMatrixPermission(c))
        : leaves.some((c) => permissionStore.checkMatrixPermission(c));
    }

    if (config.permission) {
      const permissions = Array.isArray(config.permission)
        ? config.permission
        : [config.permission];
      const ok = permissionStore.hasPermissions(permissions, requireAll);
      hasAccess = config.matrix ? hasAccess && ok : ok;
    }

    if (config.role) {
      const roles = Array.isArray(config.role) ? config.role : [config.role];
      const hasRole = permissionStore.hasRoles(roles, requireAll);
      hasAccess =
        config.permission || config.matrix ? hasAccess && hasRole : hasRole;
    }
  }

  return { hasAccess, mode };
}

function hideOrDisableElement(
  el: HTMLElement,
  shouldHide: boolean,
  mode: 'hide' | 'disable' = 'hide',
) {
  if (mode === 'hide') {
    if (shouldHide) {
      el.style.display = 'none';
      el.setAttribute('data-permission-hidden', 'true');
    } else if (el.getAttribute('data-permission-hidden') === 'true') {
      el.style.display = '';
      el.removeAttribute('data-permission-hidden');
    }
    return;
  }

  if (el instanceof HTMLButtonElement || el instanceof HTMLInputElement) {
    if (shouldHide) {
      el.disabled = true;
      el.setAttribute('data-permission-disabled', 'true');
      el.classList.add('is-disabled-by-permission');
    } else if (el.getAttribute('data-permission-disabled') === 'true') {
      el.disabled = false;
      el.removeAttribute('data-permission-disabled');
      el.classList.remove('is-disabled-by-permission');
    }
    return;
  }

  if (shouldHide) {
    el.classList.add('is-disabled-by-permission');
    el.setAttribute('data-permission-disabled', 'true');
  } else {
    el.classList.remove('is-disabled-by-permission');
    el.removeAttribute('data-permission-disabled');
  }
}

function applyPermission(el: HTMLElement, binding: DirectiveBinding<PermissionValue>) {
  const { hasAccess, mode } = resolveHasAccess(binding);
  hideOrDisableElement(el, !hasAccess, mode);
}

const permissionDirective = {
  mounted(el: HTMLElement, binding: DirectiveBinding<PermissionValue>) {
    applyPermission(el, binding);
  },
  updated(el: HTMLElement, binding: DirectiveBinding<PermissionValue>) {
    applyPermission(el, binding);
  },
};

/** v-matrix="'bom-matrix-1-9'" 或 v-matrix="{ matrix: '...', mode: 'disable' }" */
const matrixDirective = {
  mounted(el: HTMLElement, binding: DirectiveBinding<PermissionValue>) {
    const value =
      typeof binding.value === 'string' || Array.isArray(binding.value)
        ? { matrix: binding.value }
        : binding.value;
    applyPermission(el, { ...binding, value } as DirectiveBinding<PermissionValue>);
  },
  updated(el: HTMLElement, binding: DirectiveBinding<PermissionValue>) {
    const value =
      typeof binding.value === 'string' || Array.isArray(binding.value)
        ? { matrix: binding.value }
        : binding.value;
    applyPermission(el, { ...binding, value } as DirectiveBinding<PermissionValue>);
  },
};

export function setupPermissionDirective(app: App) {
  app.directive('permission', permissionDirective);
  app.directive('matrix', matrixDirective);
}

export default permissionDirective;
